/**
 * Módulo de Gestión de Usuarios y Pestañas SaaS
 * Permite crear, editar, eliminar y dar de alta/baja a usuarios en el sistema.
 * Cumple con .idx-rules.md: funciones < 40 líneas y archivo < 300 líneas.
 */

import { getAllUsers, saveUser, deleteUser } from '../infra/db.js';
import { getCurrentUser } from './router.js';
import { loadAndRenderCompanies, getCompaniesList } from './companies.js';

let usersList = [];
let userSearchQuery = '';

// Escuchar evento de recarga de la pantalla
window.addEventListener('load_users', () => {
  resetTabState();
  adjustRoleSelectOptions();
  loadAndRenderUsers();
});

function resetTabState() {
  const toggle = (id, act, disp) => {
    const el = document.getElementById(id);
    if (el) { el.classList.toggle('active', act); if (disp) el.style.display = disp; }
  };
  toggle('tab-btn-users', true);
  toggle('tab-btn-companies', false);
  toggle('tab-panel-users', true, 'block');
  toggle('tab-panel-companies', false, 'none');
}

/**
 * Ajusta los roles disponibles en el combobox según el usuario logueado.
 */
function adjustRoleSelectOptions() {
  const select = document.getElementById('usr-role');
  if (!select) return;
  const isSuper = getCurrentUser()?.role !== 'admin';
  select.innerHTML = `<option value="portero">Portero (Solo Escaneo)</option>
    <option value="admin">Administrador (Puerta y Reportes)</option>
    ${isSuper ? '<option value="superadmin">Super Administrador (SaaS Total)</option>' : ''}`;
}

/**
 * Carga la lista de usuarios y gatilla el renderizado.
 */
async function loadAndRenderUsers() {
  try {
    const currentUser = getCurrentUser();
    if (!currentUser) return;
    const isGlobalSuper = currentUser.role === 'superadmin' && currentUser.company_id === null;
    if (isGlobalSuper) {
      await loadAndRenderCompanies();
    }
    const companyId = isGlobalSuper ? null : currentUser.company_id;
    usersList = await getAllUsers(companyId);
    renderUsersTable();
  } catch (error) {
    console.error('Error al obtener usuarios:', error);
  }
}

/**
 * Renderiza una fila de usuario en formato HTML.
 */
function renderUserRow(u, isSuperadmin, companies) {
  const isSuper = u.username === 'superadmin';
  const statusClass = u.status === 'ACTIVE' ? 'badge-gold' : 'badge-red';
  const statusText = u.status === 'ACTIVE' ? 'Activo' : 'Inactivo';
  const toggleLabel = u.status === 'ACTIVE' ? 'Baja' : 'Alta';
  const toggleClass = u.status === 'ACTIVE' ? 'btn-danger' : 'btn-primary';
  const toggleIcon = u.status === 'ACTIVE' ? '🚫' : '✅';

  let companyCell = '';
  if (isSuperadmin) {
    const comp = companies.find(c => c.id === u.company_id);
    const compName = comp ? comp.name : (u.role === 'superadmin' ? 'SaaS Global' : 'Ninguna');
    companyCell = `<td data-label="Empresa">${compName}</td>`;
  }

  return `
    <tr>
      <td data-label="Usuario" class="font-mono text-sm">${u.username}</td>
      <td data-label="Nombre">${u.fullName}</td>
      ${companyCell}
      <td data-label="Rol" class="text-capitalize">${u.role}</td>
      <td data-label="Estado"><span class="badge ${statusClass}">${statusText}</span></td>
      <td data-label="Acciones">
        <div style="display: flex; gap: 8px;">
          <button class="btn btn-sm btn-secondary btn-edit-user" data-username="${u.username}">
            <span>✏️</span><span class="btn-text"> Editar</span>
          </button>
          ${!isSuper ? `
            <button class="btn btn-sm ${toggleClass} btn-toggle-user" data-username="${u.username}">
              <span>${toggleIcon}</span><span class="btn-text"> ${toggleLabel}</span>
            </button>
            <button class="btn btn-sm btn-secondary btn-delete-user" style="color: var(--danger);" data-username="${u.username}">
              <span>🗑️</span><span class="btn-text"> Eliminar</span>
            </button>
          ` : ''}
        </div>
      </td>
    </tr>
  `;
}

/**
 * Actualiza dinámicamente las columnas de cabecera en usuarios.
 */
function updateUsersTableHeader(isSuperadmin) {
  const thead = document.getElementById('table-users-head');
  if (!thead) return;
  thead.innerHTML = `
    <tr>
      <th>Usuario</th>
      <th>Nombre</th>
      ${isSuperadmin ? '<th>Empresa</th>' : ''}
      <th>Rol</th>
      <th>Estado</th>
      <th>Acciones</th>
    </tr>
  `;
}

/**
 * Renderiza los usuarios en la tabla.
 */
function renderUsersTable() {
  const tbody = document.getElementById('table-users-body');
  if (!tbody) return;
  const currentUser = getCurrentUser();
  const isGlobalSuper = currentUser && currentUser.role === 'superadmin' && currentUser.company_id === null;
  const companies = getCompaniesList();
  updateUsersTableHeader(isGlobalSuper);
  const filtered = usersList.filter(u => 
    u.username.toLowerCase().includes(userSearchQuery) || 
    u.fullName.toLowerCase().includes(userSearchQuery)
  );
  if (filtered.length === 0) {
    const colspan = isGlobalSuper ? 6 : 5;
    tbody.innerHTML = `<tr><td colspan="${colspan}" class="text-center text-muted">No se encontraron usuarios.</td></tr>`;
    return;
  }
  tbody.innerHTML = filtered.map(u => renderUserRow(u, isGlobalSuper, companies)).join('');
  setupTableActions();
}

/**
 * Configura los eventos para los botones dentro de la tabla de usuarios.
 */
function setupTableActions() {
  const tbody = document.getElementById('table-users-body');
  if (!tbody) return;
  tbody.onclick = (e) => {
    const btn = e.target.closest('button');
    if (!btn) return;
    const name = btn.getAttribute('data-username');
    const user = usersList.find(u => u.username === name);
    if (btn.classList.contains('btn-edit-user') && user) loadUserIntoForm(user);
    else if (btn.classList.contains('btn-toggle-user') && user) toggleUserStatus(user);
    else if (btn.classList.contains('btn-delete-user') && name) confirmDeleteUser(name);
  };
}

/**
 * Carga los datos de un usuario en el formulario para editarlo.
 */
function loadUserIntoForm(user) {
  const setVal = (id, val) => { const el = document.getElementById(id); if (el) el.value = val; };
  setVal('usr-old-username', user.username);
  setVal('usr-username', user.username);
  setVal('usr-fullname', user.fullName);
  setVal('usr-password', user.password);
  setVal('usr-role', user.role);
  setVal('usr-status', user.status);
  setVal('usr-company', user.company_id || '');

  document.getElementById('usr-form-title').innerText = 'Editar Usuario';
  const cancelBtn = document.getElementById('btn-users-cancel');
  if (cancelBtn) cancelBtn.style.display = 'inline-flex';
}

/**
 * Cambia el estado (Alta/Baja) de un usuario.
 */
async function toggleUserStatus(user) {
  try {
    const updatedUser = {
      ...user,
      status: user.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE',
      isEdit: true
    };
    await saveUser(updatedUser);
    alert(`Estado del usuario ${user.username} modificado correctamente.`);
    loadAndRenderUsers();
  } catch (error) {
    alert(`Error al cambiar estado: ${error.message}`);
  }
}

/**
 * Confirma y procesa la eliminación del usuario.
 */
function confirmDeleteUser(username) {
  const isLight = document.documentElement.classList.contains('light-mode');
  window.Swal.fire({
    title: '¿Eliminar usuario?',
    text: `¿Está seguro de eliminar permanentemente al usuario "${username}"?`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#ef4444',
    cancelButtonColor: '#6b7280',
    confirmButtonText: 'Sí, eliminar',
    cancelButtonText: 'Cancelar',
    background: isLight ? '#ffffff' : '#171f33',
    color: isLight ? '#0f172a' : '#dae2fd'
  }).then(async (result) => {
    if (result.isConfirmed) {
      try {
        await deleteUser(username);
        alert('Usuario eliminado correctamente.');
        loadAndRenderUsers();
      } catch (err) {
        alert(`Error al eliminar: ${err.message}`);
      }
    }
  });
}

/**
 * Procesa el envío del formulario para crear o guardar un usuario.
 */
async function handleUserFormSubmit(e) {
  e.preventDefault();
  const getVal = (id) => document.getElementById(id).value;
  const oldUsername = getVal('usr-old-username');
  const username = getVal('usr-username').trim();
  const fullName = getVal('usr-fullname').trim();
  const password = getVal('usr-password');
  const role = getVal('usr-role');
  const status = getVal('usr-status');

  const loggedInUser = getCurrentUser();
  let company_id = loggedInUser.company_id;
  if (loggedInUser.company_id === null) {
    const selectedCompany = getVal('usr-company');
    company_id = selectedCompany ? parseInt(selectedCompany, 10) : null;
  }
  if (role !== 'superadmin' && !company_id) {
    return alert('Debe asociar una empresa a este usuario.');
  }
  try {
    const isEdit = oldUsername === username;
    if (oldUsername && !isEdit) await deleteUser(oldUsername);
    await saveUser({ username, fullName, role, password, status, company_id, isEdit });
    alert('Usuario guardado exitosamente.');
    resetUserForm();
    loadAndRenderUsers();
  } catch (error) {
    alert(`Error al guardar usuario: ${error.message}`);
  }
}

function resetUserForm() {
  document.getElementById('users-form').reset();
  document.getElementById('usr-old-username').value = '';
  document.getElementById('usr-form-title').innerText = 'Crear Nuevo Usuario';
  document.getElementById('btn-users-cancel')?.style.setProperty('display', 'none');
}

// Inicializa la lógica de cambio de pestañas
function initTabs() {
  document.querySelectorAll('.btn-tab').forEach(tab => {
    tab.addEventListener('click', () => {
      document.querySelectorAll('.btn-tab').forEach(t => t.classList.remove('active'));
      tab.classList.add('active');
      document.querySelectorAll('.tab-panel-content').forEach(p => p.style.display = 'none');
      const activePanel = document.getElementById(tab.getAttribute('data-tab'));
      if (activePanel) activePanel.style.display = 'block';
    });
  });
}

// Inicializa los listeners y pestañas del módulo de usuarios
export function initUsersModule() {
  const form = document.getElementById('users-form');
  form?.addEventListener('submit', handleUserFormSubmit);

  const cancelBtn = document.getElementById('btn-users-cancel');
  cancelBtn?.addEventListener('click', resetUserForm);

  initTabs();

  const searchInput = document.getElementById('search-users');
  searchInput?.addEventListener('input', (e) => {
    userSearchQuery = e.target.value.toLowerCase().trim();
    renderUsersTable();
  });
}
